import { Box, Paper, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { INSTITUTIONS_LIST } from "../../../../enums/QueryKeys";
import { fetchInstitutions } from "../../../../enums/queryFunction/Fetch";
import { EDIT_INSTITUTIONS, DELETE_INSTITUTIONS } from "../../../../enums/PermissionTitles";
import useUser from "../../../../hooks/useUser";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import TemplatesSearchFilter from "../components/TemplatesSearchFilter";
import InstitutionTable from "./InstitutionTable";
import InstitutionFormDialog from "./InstitutionFormDialog";
import DeleteInstitutionDialog from "./DeleteInstitutionDialog";
import useSubmit from "../../../master/species/hooks/useSubmit";
import usePut from "../../../../hooks/usePut";
import * as apiPaths from "../../../../enums/ApiPaths";

const InstitutionsTab = React.forwardRef(function InstitutionsTab(props, ref) {
  const { t } = useTranslation();
  const { currentClinic } = useUser();
  const clinicId = currentClinic?.id;
  const [searchQuery, setSearchQuery] = useSearchParams("");
  const nameParam = searchQuery.get("institutionQ") ?? "";

  const {
    data: institutions,
    isLoading,
    error,
  } = useQuery({
    queryKey: [INSTITUTIONS_LIST + clinicId, nameParam],
    queryFn: () => fetchInstitutions(clinicId, nameParam),
    staleTime: Infinity,
  });
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isEditingAllowed = isPermissionAllowed(EDIT_INSTITUTIONS);
  const isDeletingAllowed = isPermissionAllowed(DELETE_INSTITUTIONS);

  const items = React.useMemo(() => (Array.isArray(institutions) ? institutions : []), [institutions]);
  const isSearchActive = nameParam !== "";

  const [formState, setFormState] = React.useState(null);
  const [institutionPendingDelete, setInstitutionPendingDelete] = React.useState(null);

  const institutionUrl = `${apiPaths.INSTITUTION}${clinicId}`;
  const create = useSubmit(null, institutionUrl, null, [INSTITUTIONS_LIST + clinicId]);
  const edit = usePut(institutionUrl, [INSTITUTIONS_LIST + clinicId], [INSTITUTIONS_LIST + clinicId]);

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ institution: null }),
  }));

  const updateQueryParam = React.useCallback(
    (value) => {
      setSearchQuery((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("institutionQ", value);
        else next.delete("institutionQ");
        return next;
      });
    },
    [setSearchQuery],
  );

  async function handleSubmit(data) {
    const isEditMode = Boolean(formState.institution);
    const obj = isEditMode ? { ...formState.institution, ...data } : data;
    const success = isEditMode
      ? await edit.submitCallback(obj)
      : await create.submitCallback(obj);
    if (success) {
      setFormState(null);
    }
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingInstitutionsData" severity="error" />;
  }

  return (
    <>
      <Stack spacing={2} mb={3}>
        <Box sx={{ display: "flex", width: "100%" }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, flex: 1, borderRadius: 3, borderColor: "divider" }}
          >
            <TemplatesSearchFilter
              initialValue={nameParam}
              onSearchChange={updateQueryParam}
            />
          </Paper>
        </Box>
      </Stack>

      <InstitutionTable
        items={items}
        isLoading={isLoading}
        emptyMessage={isSearchActive ? "NoInstitutionsFoundForGivenQuery" : "NoInstitutions"}
        canEdit={isEditingAllowed}
        canDelete={isDeletingAllowed}
        onEdit={(institution) => setFormState({ institution })}
        onDelete={(institution) => setInstitutionPendingDelete(institution)}
      />

      {formState && (
        <InstitutionFormDialog
          title={formState.institution ? t("EditInstitution") : t("AddInstitution")}
          initialValues={formState.institution}
          isSubmitting={formState.institution ? edit.loading : create.loading}
          onSubmit={handleSubmit}
          onClose={() => setFormState(null)}
        />
      )}

      <DeleteInstitutionDialog
        institution={institutionPendingDelete}
        clinicId={clinicId}
        onCancel={() => setInstitutionPendingDelete(null)}
        onDeleted={() => setInstitutionPendingDelete(null)}
      />
    </>
  );
});

export default InstitutionsTab;
